// 统计首页 — 展示服务端缓存的统计快照
// 公开指标任何人可见;详细列表(最近仓库、活跃用户)需登录后展示
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Text,
  Title2,
  Title3,
  Badge,
  Spinner,
  Button,
  ProgressBar,
} from '@fluentui/react-components';
import {
  PeopleRegular,
  AppFolderRegular,
  HistoryRegular,
  CloudRegular,
  LockClosedRegular,
  GlobeRegular,
  ArrowRightRegular,
} from '@fluentui/react-icons';
import { statsApi } from '@/api/stats';
import { useAuthStore } from '@/store/auth';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes';
import { RepoDetailDialog } from '@/components/RepoDetailDialog';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    paddingBottom: '32px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '8px',
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  // ── 数字卡片网格 ──
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '16px',
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '20px',
  },
  statIcon: {
    color: tokens.colorBrandForeground1,
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '1.2',
    color: tokens.colorNeutralForeground1,
  },
  statLabel: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  // ── 双列区域 ──
  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '16px',
  },
  listCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':last-child': {
      borderBottom: 'none',
    },
  },
  listItemText: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loginPrompt: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '32px 16px',
    textAlign: 'center',
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
  },
  progressLabel: {
    fontSize: '13px',
    minWidth: '60px',
    color: tokens.colorNeutralForeground2,
  },
  progressValue: {
    fontSize: '13px',
    fontWeight: 600,
    minWidth: '40px',
    textAlign: 'right',
  },
});

function StatCard({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: number | string;
  label: string;
}) {
  const styles = useStyles();
  return (
    <Card appearance="outline" className={styles.statCard}>
      <div className={styles.statIcon}>{icon}</div>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </Card>
  );
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '无';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return '刚刚';
    if (mins < 60) return `${mins} 分钟前`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} 小时前`;
    return `${Math.floor(hours / 24)} 天前`;
  } catch {
    return iso;
  }
}

export function StatsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const loggedIn = isAuthenticated();
  const [detail, setDetail] = useState<{ owner: string; name: string } | null>(null);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: statsApi.get,
    staleTime: 60_000,
    retry: 1,
  });

  if (isLoading || !stats) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px' }}>
        <Spinner size="large" label="加载统计数据..." />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* 标题 */}
      <div className={styles.headerRow}>
        <div>
          <Title2 as="h1">系统概览</Title2>
          <Text className={styles.subtitle}>
            数据更新于 {formatRelativeTime(stats.generated_at)}
          </Text>
        </div>
      </div>

      {/* 4 张公开数字卡片 */}
      <div className={styles.statsGrid}>
        <StatCard
          icon={<PeopleRegular fontSize={24} />}
          value={stats.total_users}
          label="用户总数"
        />
        <StatCard
          icon={<AppFolderRegular fontSize={24} />}
          value={stats.total_repos}
          label="仓库总数"
        />
        <StatCard
          icon={<HistoryRegular fontSize={24} />}
          value={stats.total_branches}
          label="分支总数"
        />
        <StatCard
          icon={<CloudRegular fontSize={24} />}
          value={stats.total_backups}
          label="备份数"
        />
      </div>

      {/* 双列:仓库分布 + 详细列表 */}
      <div className={styles.twoCol}>
        {/* 仓库可见性分布 */}
        <Card appearance="outline" className={styles.listCard}>
          <CardHeader
            header={<Title3 as="h3">仓库分布</Title3>}
          />
          <div style={{ padding: '8px 16px 16px' }}>
            <div className={styles.progressRow}>
              <GlobeRegular fontSize={16} style={{ color: tokens.colorBrandForeground1 }} />
              <span className={styles.progressLabel}>公开</span>
              <ProgressBar
                value={stats.total_repos > 0 ? stats.public_repos / stats.total_repos : 0}
                shape="rounded"
                style={{ flex: 1 }}
              />
              <span className={styles.progressValue}>{stats.public_repos}</span>
            </div>
            <div className={styles.progressRow}>
              <LockClosedRegular fontSize={16} style={{ color: tokens.colorPaletteDarkOrangeBorder1 }} />
              <span className={styles.progressLabel}>私有</span>
              <ProgressBar
                value={stats.total_repos > 0 ? stats.private_repos / stats.total_repos : 0}
                shape="rounded"
                color="warning"
                style={{ flex: 1 }}
              />
              <span className={styles.progressValue}>{stats.private_repos}</span>
            </div>
            <div style={{ marginTop: '12px' }}>
              <Text size={200} className={styles.subtitle}>
                7 日活跃用户:{stats.active_users_7d} 人
              </Text>
            </div>
          </div>
        </Card>

        {/* 最近创建的仓库 */}
        <Card appearance="outline" className={styles.listCard}>
          <CardHeader
            header={<Title3 as="h3">最近创建的仓库</Title3>}
          />
          {loggedIn ? (
            <div style={{ padding: '0 16px 16px' }}>
              {stats.recent_repos.length === 0 ? (
                <Text className={styles.subtitle}>暂无仓库</Text>
              ) : (
                stats.recent_repos.slice(0, 8).map((repo) => (
                  <div
                    key={repo.full_name}
                    className={styles.listItem}
                    onClick={() => {
                      const [owner, name] = repo.full_name.split('/');
                      if (owner && name) setDetail({ owner, name });
                    }}
                  >
                    <Text className={styles.listItemText} weight="semibold">
                      {repo.full_name}
                    </Text>
                    <Badge
                      size="small"
                      appearance={repo.visibility === 'public' ? 'filled' : 'outline'}
                      color={repo.visibility === 'public' ? 'brand' : 'warning'}
                    >
                      {repo.visibility === 'public' ? '公开' : '私有'}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className={styles.loginPrompt}>
              <Text className={styles.subtitle}>登录后查看最近仓库</Text>
              <Button
                size="small"
                icon={<ArrowRightRegular />}
                iconPosition="after"
                onClick={() => navigate(ROUTES.login)}
              >
                去登录
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* 最近注册的用户(需登录) */}
      {loggedIn && (
        <Card appearance="outline" className={styles.listCard}>
          <CardHeader
            header={<Title3 as="h3">最近注册的用户</Title3>}
          />
          <div style={{ padding: '0 16px 16px' }}>
            {stats.most_active_users.length === 0 ? (
              <Text className={styles.subtitle}>暂无用户</Text>
            ) : (
              <div className={styles.twoCol}>
                {stats.most_active_users.slice(0, 10).map((user) => (
                  <div key={user.username} className={styles.listItem}>
                    <Text className={styles.listItemText} weight="semibold">
                      {user.username}
                    </Text>
                    {user.display_name && (
                      <Text className={styles.subtitle} size={200}>
                        {user.display_name}
                      </Text>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* 仓库详情对话框 */}
      <RepoDetailDialog
        owner={detail?.owner ?? null}
        name={detail?.name ?? null}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}
